<template>
  <div class="video-player">
    <video
      :src="isHls ? undefined : getUrl(src)"
      :autoplay="autoplay"
      :loop="loop"
      class="video-player__video"
      ref="video"
      v-if="!isRPI"
      preload="auto"
      playsinline
      webkit-playsinline
    ></video>

    <VodControls
      v-if="controls && !isRPI"
      :is-visible="isOsdVisible"
      :state="playerState"
      :speed="playerSpeed"
      :current-time="playerTime"
      :duration="playerDuration"
      :title="title"
    />
  </div>
</template>

<script>
import DeviceType from '@/helpers/DeviceType';
import { VideoStates } from '.';
import VodControls from './VodControls.vue';
import RPI from '@/helpers/TV/RPI';
import { DVB_TYPE } from '@/helpers/TV';
import { queryAssembler } from '@/mixins/queryAssembler';
import VodPlayer, { PLAYER_STATE, SEEK_STEP } from '@/helpers/VodPlayer';
import { attachHls, isHlsUrl } from '@/helpers/HlsLoader';

/**
 * VoD-Player fuer Samsung (Tizen 6 - 9) und LG (webOS 5 - 25).
 *
 * Die Komponente zeigt das Bild und die Bedienleiste; die Wiedergabe selbst
 * steuert helpers/VodPlayer. Tasten wertet sie NICHT aus - das macht die Seite
 * (pages/MediaPlayer.vue) und ruft die Methoden unten ueber ein ref auf.
 * Grund: nur die Seite weiss, ob gerade der Zurueck-Knopf im Fokus steht oder
 * eine Fremdseite die Tasten braucht.
 */
export default {
  mixins: [queryAssembler],
  components: { VodControls },
  props: {
    src: {
      type: String,
      default:
        'https://temp.digmedia.net/website/video/rain_264.mp4?_=1',
    },
    /**
     * Zustand als Attribut - der alte Weg, die Wiedergabe von aussen zu
     * steuern (VideoStates.PLAY/PAUSE). Weiter unterstuetzt, damit die ueber
     * den Socket angestossenen Videos unveraendert laufen; neuer Code sollte
     * die Methoden play()/pause()/stop() ueber ein ref aufrufen.
     */
    status: String,
    autoplay: {
      type: Boolean,
      default: true,
    },
    /**
     * Darf der Gast die Wiedergabe bedienen?
     *
     * ACHTUNG, die Bedeutung hat sich geaendert: frueher landete das Attribut
     * direkt am <video>-Element und zeichnete die NATIVEN Browser-Bedienelemente.
     * Die sind auf einem Fernseher nutzlos - es gibt keinen Mauszeiger, und mit
     * der Fernbedienung sind sie nicht erreichbar. Jetzt steuert das Attribut
     * die eigene Bedienleiste (VodControls).
     *
     * false heisst weiterhin "der Gast soll nicht eingreifen" - so nutzt es
     * MediaHandler.assembleSocketVideoQuery fuer erzwungene Einspielungen.
     */
    controls: Boolean,
    loop: Boolean,
    /** Startposition in Sekunden (z.B. Wiederaufnahme aus der Mediathek). */
    seekTo: {
      type: [Number, String],
      default: 0,
    },
    /** Titel fuer die Bedienleiste. */
    title: String,
    description: String,
    /**
     * Abspielposition als v-model-Gegenstueck zum input-Ereignis. Die
     * Komponente liest den Wert NICHT - sie meldet ihre Position nach oben.
     * Deklariert, damit er nicht als HTML-Attribut am Wurzelelement landet.
     */
    value: Number,
    /**
     * Bedienleiste gerade sichtbar?
     *
     * Kommt von der Seite, damit Leiste und Kopfzeile (OSDHeader) gemeinsam
     * ein- und ausblenden - die Seite kennt die Zeitsteuerung, die Komponente
     * nicht.
     */
    isOsdVisible: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['status', 'setVideo', 'input', 'state', 'ended', 'error'],
  data() {
    return {
      player: null,
      playerState: PLAYER_STATE.IDLE,
      playerSpeed: 1,
      playerTime: 0,
      playerDuration: 0,
      hasSeekedToStart: false,
      // Laufender hls.js-Anschluss (siehe startSource) - beim Quellenwechsel
      // und beim Abbau zu zerstoeren.
      hlsHandle: null,
      hlsPending: false,
      pendingPlay: false,
    };
  },
  mounted() {
    if (this.$refs.video) {
      this.player = new VodPlayer({ onChange: this.handlePlayerChange });
      this.player.attach(this.$refs.video);
      this.$emit('setVideo', this.$refs.video);
      this.startSource();
    }
    if (this.isRPI) {
      RPI.setChannel({
        dvb_type: DVB_TYPE.IP,
        url: this.src,
      });
    }
  },
  beforeDestroy() {
    // Ohne detach() laeuft der Spul-Takt weiter, nachdem die Seite verlassen
    // wurde - er greift dann auf ein Element zu, das Vue schon abgebaut hat.
    this.stopHls();
    if (this.player) {
      this.player.detach();
      this.player = null;
    }
  },
  methods: {
    getUrl(src) {
      return this.assembleUrl(src);
    },

    // ---- Steuerung, von der Seite ueber ein ref aufgerufen -----------------

    play() {
      // HLS wird noch angehaengt (hls.js laedt): merken und nach dem Anhaengen
      // starten. Ein play() auf dem noch leeren Element liefe ins Leere - die
      // Seite ruft es beim Oeffnen ueber das status-Attribut, bevor hls.js da ist.
      if (this.hlsPending) {
        this.pendingPlay = true;
        return Promise.resolve(false);
      }
      return this.player && this.player.play();
    },
    pause() {
      return this.player && this.player.pause();
    },
    togglePlayPause() {
      return this.player && this.player.togglePlayPause();
    },
    stop() {
      return this.player && this.player.stop();
    },
    fastForward() {
      return this.player && this.player.fastForward();
    },
    rewind() {
      return this.player && this.player.rewind();
    },
    /** Sprung um SEEK_STEP Sekunden vor (LINKS/RECHTS auf der Fernbedienung). */
    stepForward() {
      return this.player && this.player.seekBy(SEEK_STEP);
    },
    stepBackward() {
      return this.player && this.player.seekBy(-SEEK_STEP);
    },
    seekBy(seconds) {
      return this.player && this.player.seekBy(seconds);
    },
    seekToPosition(seconds) {
      return this.player && this.player.seekTo(seconds);
    },
    // ---- Innere Ablaeufe ---------------------------------------------------

    /**
     * Quelle starten.
     *
     * HLS (.m3u8) laeuft ueber hls.js (helpers/HlsLoader): das src-Attribut
     * bleibt dann leer, hls.js speist das Element ueber Media Source
     * Extensions. Ohne MSE oder ohne ladbares hls.js faellt es auf das native
     * HLS des Elements zurueck (Tizen, webOS). Alles andere (MP4 & Co.) steht
     * wie bisher direkt im src.
     */
    startSource() {
      const video = this.$refs.video;
      if (!video) return;
      this.stopHls();
      if (!this.isHls) {
        this.applyStartPosition();
        if (this.autoplay) this.play();
        return;
      }
      const src = this.src;
      this.hlsPending = true;
      this.pendingPlay = !!this.autoplay;
      attachHls(video, this.getUrl(src)).then((handle) => {
        // Inzwischen abgebaut oder Quelle gewechselt: verwerfen.
        if (!this.$refs.video || this.src !== src) {
          handle.destroy();
          return;
        }
        this.hlsPending = false;
        this.hlsHandle = handle;
        this.applyStartPosition();
        if (this.pendingPlay) {
          this.pendingPlay = false;
          this.play();
        }
      });
    },
    stopHls() {
      if (this.hlsHandle) {
        this.hlsHandle.destroy();
        this.hlsHandle = null;
      }
    },

    /**
     * Springt einmalig auf die uebergebene Startposition.
     *
     * Erst wenn die Kopfdaten da sind: vorher ist duration unbekannt, und
     * currentTime laesst sich auf beiden Plattformen noch nicht setzen - der
     * Sprung ginge kommentarlos verloren.
     */
    applyStartPosition() {
      const start = Number(this.seekTo);
      if (!start || this.hasSeekedToStart) return;
      const video = this.$refs.video;
      if (!video) return;
      const apply = () => {
        this.hasSeekedToStart = true;
        this.player && this.player.seekTo(start);
      };
      if (video.readyState >= 1) {
        apply();
        return;
      }
      video.addEventListener('loadedmetadata', apply, { once: true });
    },

    handlePlayerChange(snapshot) {
      this.playerState = snapshot.state;
      this.playerSpeed = snapshot.speed;
      this.playerTime = snapshot.currentTime;
      this.playerDuration = snapshot.duration;

      this.$emit('state', snapshot);
      this.$emit('input', snapshot.currentTime);
      this.$emit('status', snapshot.state);
      if (snapshot.state === PLAYER_STATE.ENDED) this.$emit('ended');
      if (snapshot.state === PLAYER_STATE.ERROR) this.$emit('error');
    },

    /** Der alte Weg ueber das status-Attribut. */
    statusUpdateHandling(value) {
      if (!this.player) return;
      switch (value) {
        case VideoStates.PLAY:
          // Ueber play(), nicht direkt am Player: so wartet es auf hls.js.
          this.play();
          break;
        case VideoStates.PAUSE:
          this.player.pause();
          break;
        case VideoStates.STOPPED:
          this.player.stop();
          break;
        case VideoStates.FORWARD:
          this.player.fastForward();
          break;
        case VideoStates.REWIND:
          this.player.rewind();
          break;
      }
    },
  },
  computed: {
    isRPI() {
      return DeviceType.isRPI;
    },
    isHls() {
      return isHlsUrl(this.src);
    },
  },
  watch: {
    status(value) {
      // Nur reagieren, wenn das Attribut wirklich etwas ANDERES verlangt.
      // Die Seite spiegelt den gemeldeten Zustand zurueck in dieses Attribut -
      // ohne die Abfrage wuerde jede Meldung des Players als neuer Befehl
      // zurueckkommen und das Spulen sofort wieder beenden.
      if (value === this.playerState) return;
      this.statusUpdateHandling(value);
    },
    src() {
      // Quellenwechsel bei stehender Komponente: aeltere webOS-Staende laden
      // die neue Adresse nicht von selbst, load() ist dort Pflicht.
      const video = this.$refs.video;
      if (!video) return;
      this.hasSeekedToStart = false;
      if (!this.isHls) {
        try {
          video.load();
        } catch (e) {
          console.log('[VideoPlayer] load', e);
        }
      }
      this.startSource();
    },
  },
};
</script>

<style scoped>
/*
 * Immer Vollbild, unabhaengig von der Aufloesung der Quelle.
 *
 * Gemeldet 16.10.2026: ein 720p-HLS-Stream lief auf einem 1080p-TV nur klein
 * in der Mitte. Das Element folgte dort seiner natuerlichen Groesse
 * (1280x720) statt der Flaeche - die Prozentangaben haengen am Elternelement,
 * und das ist je nach Seite und Engine nicht verlaesslich bildschirmgross.
 * Deshalb hier fest an den Bildschirm geheftet (fixed, alle Kanten 0) und das
 * Video absolut in die volle Flaeche gelegt; object-fit skaliert hoch und
 * haelt das Seitenverhaeltnis.
 */
.video-player {
  background-color: black;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
}
.video-player__video {
  position: absolute;
  top: 0;
  left: 0;
  display: block;
  width: 100%;
  height: 100%;
  max-width: none;
  max-height: none;
  object-fit: contain;
  background-color: black;
}
</style>



// WEBPACK FOOTER //
// src/components/VideoPlayer/VideoPlayer.vue